import { CarrierVisitStatusChip } from '@planning/components/CarrierVisitStatusChip'
import { getVesselTypeLabel } from '@planning/pages/Vessel/components/Molecules/VesselForm'
import { formatDateShort, formatTime } from '@planning/utils'
import { TFnType } from '@tolgee/react'
import {
  getMultiSelectRenderBadge,
  MultiLineTextCell,
  TitleWithCaptionCell,
  TomTableEmptyCell,
} from '@tom-ui/ui'
import { MRT_ColumnDef } from 'material-react-table'
import { VesselVisitTableDto } from '../Stores/VesselVisitViewStore'
import { columnFilterPresets } from './columnFilterConfigs'

export const getDateAndLabel = (
  actual?: string | null,
  estimated?: string | null,
  labelWhenActual?: string,
  labelWhenEstimated?: string,
) => {
  const time = actual ?? estimated

  if (!time) {
    return { date: '-', caption: '-' }
  }

  const date = formatDateShort(time)
  const timeFormatted = formatTime(time)
  const label = actual ? labelWhenActual : labelWhenEstimated

  return {
    date: date ?? '-',
    caption: `${timeFormatted} ${label}`,
  }
}

export const getLVesselVisitListColumns = (t: TFnType): MRT_ColumnDef<VesselVisitTableDto>[] => [
  {
    ...columnFilterPresets.searchMandatory(),
    accessorKey: 'number',
    header: t('No', 'No'),
    size: 200,
  },
  {
    ...columnFilterPresets.searchMandatory(),
    accessorKey: 'identifier',
    header: t('vessel', 'Vessel'),
    size: 200,
  },
  {
    ...columnFilterPresets.vesselVisitStatusMultiSelect(),
    accessorKey: 'status',
    header: t('status', 'Status'),
    size: 200,
    Cell: cell => (
      <CarrierVisitStatusChip
        status={cell.row.original.status}
        isBerthed={cell.row.original.atb != null}
      />
    ),
    muiFilterTextFieldProps: ({ column }) =>
      getMultiSelectRenderBadge(column, t, t('filterByStatus', 'Filter by Status')),
  },
  {
    ...columnFilterPresets.vesselTypeMultiSelect(),
    header: t('Type', 'Type'),
    accessorKey: 'vesselType',
    size: 200,
    Cell: ({ cell }) => getVesselTypeLabel(cell.row.original.vesselType, t),
    muiFilterTextFieldProps: ({ column }) =>
      getMultiSelectRenderBadge(column, t, t('filterByType', 'Filter by Type')),
  },
  {
    ...columnFilterPresets.numericMandatory(),
    accessorKey: 'vesselLength',
    header: t('length', 'Length (m)'),
    size: 200,
  },
  {
    ...columnFilterPresets.searchMandatory(),
    accessorKey: 'operatorName',
    header: t('operator', 'Operator'),
    size: 200,
  },
  {
    ...columnFilterPresets.searchMandatory(),
    accessorKey: 'agentName',
    header: t('agent', 'Agent'),
    size: 200,
  },
  {
    ...columnFilterPresets.searchMandatory(),
    accessorKey: 'importVoyage',
    header: t('importVoyageNumbers', 'Imp. voy'),
    size: 200,
    Cell: ({ renderedCellValue }: { renderedCellValue: React.ReactNode }) => {
      return <MultiLineTextCell value={renderedCellValue} />
    },
  },
  {
    ...columnFilterPresets.searchMandatory(),
    accessorKey: 'exportVoyage',
    header: t('exportVoyageNumbers', 'Exp. voy'),
    size: 200,
    Cell: ({ renderedCellValue }: { renderedCellValue: React.ReactNode }) => {
      return <MultiLineTextCell value={renderedCellValue} />
    },
  },
  {
    ...columnFilterPresets.searchOptional(),
    accessorKey: 'portCallId',
    header: t('portCallId', 'Port Call ID'),
    minSize: 250,
    maxSize: 480,
    Cell: TomTableEmptyCell,
  },
  {
    ...columnFilterPresets.dateMandatory(),
    accessorKey: 'eta',
    header: t('eta/ata', 'ETA/ATA'),
    minSize: 250,
    maxSize: 480,
    accessorFn: row => row.ata ?? row.eta,
    Cell: cell => {
      const { date, caption } = getDateAndLabel(
        cell.row.original.ata,
        cell.row.original.eta,
        'ATA',
        'ETA',
      )
      return <TitleWithCaptionCell title={date} caption={caption} />
    },
    enableGlobalFilter: false,
  },
  {
    ...columnFilterPresets.dateOptional(),
    accessorKey: 'etb',
    header: t('etb/atb', 'ETB/ATB'),
    minSize: 250,
    maxSize: 480,
    accessorFn: row => row.atb ?? row.etb,
    Cell: cell => {
      const { date, caption } = getDateAndLabel(
        cell.row.original.atb,
        cell.row.original.etb,
        'ATB',
        'ETB',
      )
      return <TitleWithCaptionCell title={date} caption={caption} />
    },
    enableGlobalFilter: false,
    // filterFn: 'equals',
  },
  {
    ...columnFilterPresets.dateMandatory(),
    accessorKey: 'etd',
    header: t('etd/atd', 'ETD/ATD'),
    size: 250,
    accessorFn: row => row.atd ?? row.etd,
    Cell: cell => {
      const { date, caption } = getDateAndLabel(
        cell.row.original.atd,
        cell.row.original.etd,
        'ATD',
        'ETD',
      )
      return <TitleWithCaptionCell title={date} caption={caption} />
    },
    enableGlobalFilter: false,
  },
  {
    ...columnFilterPresets.searchMandatory(),
    accessorKey: 'berthNames',
    header: t('berth', 'Berth'),
    size: 200,
    Cell: ({ renderedCellValue }: { renderedCellValue: React.ReactNode }) => {
      return <MultiLineTextCell value={renderedCellValue} />
    },
  },
  {
    ...columnFilterPresets.numericMandatory(),
    accessorKey: 'moves',
    header: t('moves', 'Moves'),
    size: 200,
  },
]
